import styled from '@emotion/styled';
import { Typography } from '@innovamat/glimmer-components';
import { useTranslation } from 'react-i18next';
import type { CardStatus } from '../../modules/fluency/deck-report';
import CellFluency from '../cell-fluency';
import { CELL_VARIANT } from '../cell-fluency/cell-fluency';

type LegendItemProps = {
  status: CardStatus;
  label: string;
  allCellsLocked?: boolean;
};

export type ModeLegend = 'allClassroomMode' | 'studentMode';

type Props = {
  allCellsLocked?: boolean;
  mode?: ModeLegend;
};

const LegendContainer = styled.div<{ mode: ModeLegend }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  gap: 24px;
`;

const LegendGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LegendItemWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-start;
  gap: 8px;
`;

const StyledLegendText = styled(Typography.Body2)<{ allCellsLocked?: boolean }>`
  color: ${({ allCellsLocked, theme }) =>
    allCellsLocked
      ? `${theme.tokens.color.alias.cm.text['text-disabled'].value}`
      : `${theme.tokens.color.alias.cm.text['text'].value}`};
`;

const LegendItem = ({
  status,
  label,
  allCellsLocked,
}: LegendItemProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <LegendItemWrapper>
      <CellFluency
        variant={CELL_VARIANT.CELL_SMALL_SIZE}
        status={status}
        allCellsLocked={allCellsLocked}
      />
      <StyledLegendText allCellsLocked={allCellsLocked}>
        {t(label)}
      </StyledLegendText>
    </LegendItemWrapper>
  );
};

function LegendFluency({
  mode = 'allClassroomMode',
  allCellsLocked,
}: Props): JSX.Element {
  const statusLabels: Partial<Record<CardStatus, string>> = {
    notSeen: 'fluency.reports.legend.gray',
    lowParticipation: 'fluency.reports.legend.darkGrey',
    fairlyFailed: 'fluency.reports.legend.orange',
    mostlyFailed:
      mode === 'allClassroomMode'
        ? 'fluency.reports.legend.red'
        : 'fluency.reports.legend.redStudent',
    inProgress: 'fluency.reports.legend.lightGreen',
    memorized: 'fluency.reports.legend.darkGreen',
  };

  const filteredStatusLabels = Object.entries(statusLabels).filter(
    ([status]) =>
      !(mode === 'studentMode' && status === 'fairlyFailed') &&
      (mode === 'allClassroomMode' || status !== 'lowParticipation')
  );

  const renderItems = (statuses: [string, string][]): JSX.Element[] =>
    statuses.map(([status, label]) => (
      <LegendItem
        key={status}
        status={status as CardStatus}
        label={label}
        allCellsLocked={allCellsLocked}
      />
    ));

  const participationStatuses = filteredStatusLabels.filter(
    ([status]) => status === 'notSeen' || status === 'lowParticipation'
  );

  const progressStatuses = filteredStatusLabels.filter(
    ([status]) => status !== 'notSeen' && status !== 'lowParticipation'
  );

  return (
    <LegendContainer mode={mode}>
      <LegendGroup>{renderItems(participationStatuses)}</LegendGroup>
      <LegendGroup>{renderItems(progressStatuses)}</LegendGroup>
    </LegendContainer>
  );
}

export default LegendFluency;
